import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@os/components/ui/tabs';
import { Label } from '@os/components/ui/label';
import { Input } from '@os/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@os/components/ui/select';
import { Switch } from '@os/components/ui/switch';
import { Slider } from '@os/components/ui/slider';
import { Button } from '@os/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Separator } from '@os/components/ui/separator';
import { useToast } from '@os/hooks/use-toast';
import { useSettings } from '@os/hooks/useSettings';
import { 
  Palette, 
  Type, 
  Settings, 
  Eye, 
  Code2, 
  FileText, 
  Layout,
  Monitor,
  Zap,
  RotateCcw
} from 'lucide-react';

export interface EditorSettingsConfig {
  // Theme
  theme: 'light' | 'dark' | 'high-contrast-dark' | 'high-contrast-light' | 'monokai' | 'solarized-dark' | 'solarized-light' | 'material-dark' | 'dracula' | 'github-dark' | 'github-light' | 'one-dark-pro' | 'cobalt' | 'night-owl' | 'abyss' | 'winter-is-coming' | 'night-blue';
  
  // Font
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold' | '300' | '400' | '500' | '600' | '700';
  lineHeight: number;
  letterSpacing: number;
  
  // Editor behavior
  wordWrap: 'on' | 'off' | 'wordWrapColumn';
  wordWrapColumn: number;
  minimap: boolean;
  minimapScale: number;
  lineNumbers: 'on' | 'off' | 'relative' | 'interval';
  lineNumbersMinChars: number;
  cursorStyle: 'line' | 'block' | 'underline' | 'line-thin' | 'block-outline' | 'underline-thin';
  cursorBlinking: 'blink' | 'smooth' | 'phase' | 'expand' | 'solid';
  
  // Indentation
  tabSize: number;
  insertSpaces: boolean;
  detectIndentation: boolean;
  trimAutoWhitespace: boolean;
  
  // UI
  showFoldingControls: 'always' | 'mouseover' | 'never';
  showLineNumbers: boolean;
  showWhitespace: 'none' | 'boundary' | 'selection' | 'trailing' | 'all';
  renderControlCharacters: boolean;
  renderIndentGuides: boolean;
  highlightActiveIndentGuide: boolean;
  
  // Performance
  scrollBeyondLastLine: boolean;
  smoothScrolling: boolean;
  autoClosingBrackets: 'always' | 'languageDefined' | 'beforeWhitespace' | 'never';
  autoClosingQuotes: 'always' | 'languageDefined' | 'beforeWhitespace' | 'never';
  autoSurround: 'languageDefined' | 'quotes' | 'brackets' | 'never';
  
  // File explorer
  explorerCompactFolders: boolean;
  explorerSortOrder: 'default' | 'mixed' | 'filesFirst' | 'type' | 'modified';
}

const defaultSettings: EditorSettingsConfig = {
  theme: 'dark',
  fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', monospace",
  fontSize: 14,
  fontWeight: '400',
  lineHeight: 1.6,
  letterSpacing: 0,
  wordWrap: 'on',
  wordWrapColumn: 120,
  minimap: true,
  minimapScale: 1,
  lineNumbers: 'on',
  lineNumbersMinChars: 5,
  cursorStyle: 'line',
  cursorBlinking: 'blink',
  tabSize: 2,
  insertSpaces: true,
  detectIndentation: true,
  trimAutoWhitespace: true,
  showFoldingControls: 'mouseover',
  showLineNumbers: true,
  showWhitespace: 'none',
  renderControlCharacters: false,
  renderIndentGuides: true,
  highlightActiveIndentGuide: true,
  scrollBeyondLastLine: true,
  smoothScrolling: true,
  autoClosingBrackets: 'languageDefined',
  autoClosingQuotes: 'languageDefined',
  autoSurround: 'languageDefined',
  explorerCompactFolders: true,
  explorerSortOrder: 'default'
};

const themes = [
  { id: 'light', name: 'Light (Default)', description: 'Default light theme' },
  { id: 'dark', name: 'Dark (Default)', description: 'Default dark theme' },
  { id: 'high-contrast-dark', name: 'Dark High Contrast', description: 'High contrast dark theme' },
  { id: 'high-contrast-light', name: 'Light High Contrast', description: 'High contrast light theme' },
  { id: 'monokai', name: 'Monokai', description: 'Popular dark theme with bright colors' },
  { id: 'solarized-dark', name: 'Solarized Dark', description: 'Sophisticated dark theme' },
  { id: 'solarized-light', name: 'Solarized Light', description: 'Sophisticated light theme' },
  { id: 'material-dark', name: 'Material Dark', description: 'Google Material Design dark theme' },
  { id: 'dracula', name: 'Dracula', description: 'Dark theme with purple accents' },
  { id: 'github-dark', name: 'GitHub Dark', description: 'GitHub\'s dark theme for code' },
  { id: 'github-light', name: 'GitHub Light', description: 'GitHub\'s light theme for code' },
  { id: 'one-dark-pro', name: 'One Dark Pro', description: 'Atom\'s iconic One Dark theme' },
  { id: 'cobalt', name: 'Cobalt Blue', description: 'Deep blue theme perfect for long coding sessions' },
  { id: 'night-owl', name: 'Night Owl', description: 'Dark blue theme optimized for night coding' },
  { id: 'abyss', name: 'Abyss', description: 'Deep dark theme with blue accents' },
  { id: 'winter-is-coming', name: 'Winter is Coming', description: 'Cool blue winter theme' },
  { id: 'night-blue', name: 'Night Blue', description: 'Elegant dark blue theme for productivity' }
];

const fontFamilies = [
  "'JetBrains Mono', monospace",
  "'Fira Code', monospace", 
  "'Source Code Pro', monospace",
  "'Consolas', monospace",
  "'Monaco', monospace",
  "'Menlo', monospace",
  "'Ubuntu Mono', monospace",
  "'Roboto Mono', monospace",
  "'Inconsolata', monospace",
  "'Cascadia Code', monospace"
];

const EditorSettings: React.FC = () => {
  const { settings, saveSettings } = useSettings('editor-settings', defaultSettings);
  const [tempSettings, setTempSettings] = useState<EditorSettingsConfig>(settings as EditorSettingsConfig);
  const { toast } = useToast();

  // Update temp settings when main settings change
  useEffect(() => {
    setTempSettings(settings as EditorSettingsConfig);
  }, [settings]);

  const handleSave = () => {
    // Save to Supabase via useSettings hook
    saveSettings(tempSettings);
    
    // Broadcast settings change to any listening Monaco editors
    window.dispatchEvent(new CustomEvent('editorSettingsChanged', {
      detail: tempSettings
    }));
    
    toast({
      title: "Settings saved",
      description: "Your editor settings have been applied.",
    });
  };

  const handleReset = () => {
    setTempSettings(defaultSettings);
    toast({
      title: "Settings reset",
      description: "All settings have been reset to defaults.",
    });
  };

  const updateSetting = <K extends keyof EditorSettingsConfig>(key: K, value: EditorSettingsConfig[K]) => {
    const newSettings = { ...tempSettings, [key]: value };
    setTempSettings(newSettings);
    
    // Apply all changes immediately for real-time preview
    window.dispatchEvent(new CustomEvent('editorSettingsChanged', {
      detail: newSettings
    }));
  };

  return (
    <div className="h-full flex flex-col p-4">
      <Tabs defaultValue="appearance" className="flex-1 overflow-hidden">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="appearance" className="flex items-center gap-1">
            <Palette className="w-4 h-4" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="editor" className="flex items-center gap-1">
            <Code2 className="w-4 h-4" />
            Editor
          </TabsTrigger>
          <TabsTrigger value="font" className="flex items-center gap-1">
            <Type className="w-4 h-4" />
            Font
          </TabsTrigger>
          <TabsTrigger value="behavior" className="flex items-center gap-1">
            <Zap className="w-4 h-4" />
            Behavior
          </TabsTrigger>
          <TabsTrigger value="explorer" className="flex items-center gap-1">
            <Layout className="w-4 h-4" />
            Explorer
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-auto mt-4">
          <TabsContent value="appearance" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="w-4 h-4" />
                  Color Theme
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {themes.map((theme) => (
                    <div
                      key={theme.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all hover:border-primary/50 ${
                        tempSettings.theme === theme.id ? 'border-primary bg-primary/5' : ''
                      }`}
                      onClick={() => updateSetting('theme', theme.id as any)}
                    >
                      <div className="font-medium">{theme.name}</div>
                      <div className="text-sm text-muted-foreground">{theme.description}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="font" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Font Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Font Family</Label>
                    <Select 
                      value={tempSettings.fontFamily} 
                      onValueChange={(value) => updateSetting('fontFamily', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[10000]">
                        {fontFamilies.map((font) => (
                          <SelectItem key={font} value={font}>
                            <span style={{ fontFamily: font }}>
                              {font.split(',')[0].replace(/'/g, '')}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Font Weight</Label>
                    <Select 
                      value={tempSettings.fontWeight} 
                      onValueChange={(value) => updateSetting('fontWeight', value as any)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[10000]">
                        <SelectItem value="300">Light (300)</SelectItem>
                        <SelectItem value="400">Normal (400)</SelectItem>
                        <SelectItem value="500">Medium (500)</SelectItem>
                        <SelectItem value="600">Semi Bold (600)</SelectItem>
                        <SelectItem value="700">Bold (700)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label>Font Size: {tempSettings.fontSize}px</Label>
                    <Slider
                      value={[tempSettings.fontSize]}
                      onValueChange={([value]) => updateSetting('fontSize', value)}
                      min={8}
                      max={24}
                      step={1}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Line Height: {tempSettings.lineHeight}</Label>
                    <Slider
                      value={[tempSettings.lineHeight]}
                      onValueChange={([value]) => updateSetting('lineHeight', value)}
                      min={1.0}
                      max={2.0}
                      step={0.1}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Letter Spacing: {tempSettings.letterSpacing}px</Label>
                    <Slider
                      value={[tempSettings.letterSpacing]}
                      onValueChange={([value]) => updateSetting('letterSpacing', value)}
                      min={-2}
                      max={2}
                      step={0.1}
                    />
                  </div>
                </div>

                <div className="p-4 bg-muted rounded-lg" style={{
                  fontFamily: tempSettings.fontFamily,
                  fontSize: `${tempSettings.fontSize}px`,
                  fontWeight: tempSettings.fontWeight,
                  lineHeight: tempSettings.lineHeight,
                  letterSpacing: `${tempSettings.letterSpacing}px`
                }}>
                  <div className="text-muted-foreground text-sm mb-2">Preview:</div>
                  <code>
                    {`function hello() {\n  console.log("Hello, World!");\n  return true;\n}`}
                  </code>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="editor" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Display</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Line Numbers</Label>
                      <Switch
                        checked={tempSettings.showLineNumbers}
                        onCheckedChange={(checked) => updateSetting('showLineNumbers', checked)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Line Numbers Style</Label>
                      <Select 
                        value={tempSettings.lineNumbers} 
                        onValueChange={(value) => updateSetting('lineNumbers', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="on">On</SelectItem>
                          <SelectItem value="off">Off</SelectItem>
                          <SelectItem value="relative">Relative</SelectItem>
                          <SelectItem value="interval">Interval</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Line Numbers Min Chars: {tempSettings.lineNumbersMinChars}</Label>
                      <Slider
                        value={[tempSettings.lineNumbersMinChars]}
                        onValueChange={([value]) => updateSetting('lineNumbersMinChars', value)}
                        min={1}
                        max={10}
                        step={1}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Minimap</Label>
                      <Switch
                        checked={tempSettings.minimap}
                        onCheckedChange={(checked) => updateSetting('minimap', checked)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Minimap Scale: {tempSettings.minimapScale}x</Label>
                      <Slider
                        value={[tempSettings.minimapScale]}
                        onValueChange={([value]) => updateSetting('minimapScale', value)}
                        min={0.5}
                        max={3}
                        step={0.1}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Render Indent Guides</Label>
                      <Switch
                        checked={tempSettings.renderIndentGuides}
                        onCheckedChange={(checked) => updateSetting('renderIndentGuides', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Highlight Active Indent Guide</Label>
                      <Switch
                        checked={tempSettings.highlightActiveIndentGuide}
                        onCheckedChange={(checked) => updateSetting('highlightActiveIndentGuide', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Render Control Characters</Label>
                      <Switch
                        checked={tempSettings.renderControlCharacters}
                        onCheckedChange={(checked) => updateSetting('renderControlCharacters', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Scroll Beyond Last Line</Label>
                      <Switch
                        checked={tempSettings.scrollBeyondLastLine}
                        onCheckedChange={(checked) => updateSetting('scrollBeyondLastLine', checked)}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Word Wrap</Label>
                      <Select 
                        value={tempSettings.wordWrap} 
                        onValueChange={(value) => updateSetting('wordWrap', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="off">Off</SelectItem>
                          <SelectItem value="on">On</SelectItem>
                          <SelectItem value="wordWrapColumn">At Column</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Word Wrap Column: {tempSettings.wordWrapColumn}</Label>
                      <Slider
                        value={[tempSettings.wordWrapColumn]}
                        onValueChange={([value]) => updateSetting('wordWrapColumn', value)}
                        min={40}
                        max={200}
                        step={10}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Show Whitespace</Label>
                      <Select 
                        value={tempSettings.showWhitespace} 
                        onValueChange={(value) => updateSetting('showWhitespace', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="boundary">Boundary</SelectItem>
                          <SelectItem value="selection">Selection</SelectItem>
                          <SelectItem value="trailing">Trailing</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Cursor Style</Label>
                      <Select 
                        value={tempSettings.cursorStyle} 
                        onValueChange={(value) => updateSetting('cursorStyle', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="line">Line</SelectItem>
                          <SelectItem value="block">Block</SelectItem>
                          <SelectItem value="underline">Underline</SelectItem>
                          <SelectItem value="line-thin">Line Thin</SelectItem>
                          <SelectItem value="block-outline">Block Outline</SelectItem>
                          <SelectItem value="underline-thin">Underline Thin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Cursor Blinking</Label>
                      <Select 
                        value={tempSettings.cursorBlinking} 
                        onValueChange={(value) => updateSetting('cursorBlinking', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="blink">Blink</SelectItem>
                          <SelectItem value="smooth">Smooth</SelectItem>
                          <SelectItem value="phase">Phase</SelectItem>
                          <SelectItem value="expand">Expand</SelectItem>
                          <SelectItem value="solid">Solid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Folding Controls</Label>
                      <Select 
                        value={tempSettings.showFoldingControls} 
                        onValueChange={(value) => updateSetting('showFoldingControls', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="always">Always</SelectItem>
                          <SelectItem value="mouseover">On Mouseover</SelectItem>
                          <SelectItem value="never">Never</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="behavior" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Indentation & Auto-completion</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Tab Size: {tempSettings.tabSize}</Label>
                      <Slider
                        value={[tempSettings.tabSize]}
                        onValueChange={([value]) => updateSetting('tabSize', value)}
                        min={1}
                        max={8}
                        step={1}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Insert Spaces</Label>
                      <Switch
                        checked={tempSettings.insertSpaces}
                        onCheckedChange={(checked) => updateSetting('insertSpaces', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Detect Indentation</Label>
                      <Switch
                        checked={tempSettings.detectIndentation}
                        onCheckedChange={(checked) => updateSetting('detectIndentation', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Trim Auto Whitespace</Label>
                      <Switch
                        checked={tempSettings.trimAutoWhitespace}
                        onCheckedChange={(checked) => updateSetting('trimAutoWhitespace', checked)}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Auto Closing Brackets</Label>
                      <Select 
                        value={tempSettings.autoClosingBrackets} 
                        onValueChange={(value) => updateSetting('autoClosingBrackets', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="always">Always</SelectItem>
                          <SelectItem value="languageDefined">Language Defined</SelectItem>
                          <SelectItem value="beforeWhitespace">Before Whitespace</SelectItem>
                          <SelectItem value="never">Never</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Auto Closing Quotes</Label>
                      <Select 
                        value={tempSettings.autoClosingQuotes} 
                        onValueChange={(value) => updateSetting('autoClosingQuotes', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="always">Always</SelectItem>
                          <SelectItem value="languageDefined">Language Defined</SelectItem>
                          <SelectItem value="beforeWhitespace">Before Whitespace</SelectItem>
                          <SelectItem value="never">Never</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Auto Surround Selection</Label>
                      <Select 
                        value={tempSettings.autoSurround} 
                        onValueChange={(value) => updateSetting('autoSurround', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="languageDefined">Language Defined</SelectItem>
                          <SelectItem value="quotes">Quotes</SelectItem>
                          <SelectItem value="brackets">Brackets</SelectItem>
                          <SelectItem value="never">Never</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Smooth Scrolling</Label>
                      <Switch
                        checked={tempSettings.smoothScrolling}
                        onCheckedChange={(checked) => updateSetting('smoothScrolling', checked)}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="explorer" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>File Explorer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Compact Folders</Label>
                  <Switch
                    checked={tempSettings.explorerCompactFolders}
                    onCheckedChange={(checked) => updateSetting('explorerCompactFolders', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Select 
                    value={tempSettings.explorerSortOrder} 
                    onValueChange={(value) => updateSetting('explorerSortOrder', value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[10000]">
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="mixed">Mixed</SelectItem>
                      <SelectItem value="filesFirst">Files First</SelectItem>
                      <SelectItem value="type">By Type</SelectItem>
                      <SelectItem value="modified">By Modified Date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </div>

        <Separator />
        
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
          <div className="flex gap-2">
            <Button onClick={handleSave}>
              Save Settings
            </Button>
          </div>
        </div>
      </Tabs>
    </div>
  );
};

export default EditorSettings;
export { defaultSettings };